/**
 * Shared KPI/stat card. Replaces 4+ local implementations across pages.
 * Supports icon, trend indicator, and compact mobile variant.
 */

import type { LucideIcon } from 'lucide-react'
import { TrendingUp, TrendingDown } from 'lucide-react'

interface KpiCardProps {
  label: string
  value: string | number
  icon?: LucideIcon
  iconColor?: string
  iconBg?: string
  trend?: { value: string; positive: boolean }
  subtitle?: string
  className?: string
  compact?: boolean
  onClick?: () => void
}

export default function KpiCard({
  label,
  value,
  icon: Icon,
  iconColor = 'text-indigo-600',
  iconBg = 'bg-indigo-50',
  trend,
  subtitle,
  className = '',
  compact = false,
  onClick,
}: KpiCardProps) {
  const Wrapper = onClick ? 'button' : 'div'
  const padding = compact ? 'p-3' : 'p-4 sm:p-5'

  return (
    <Wrapper
      onClick={onClick}
      className={`bg-white rounded-xl border border-gray-200 ${padding} text-left transition-colors ${
        onClick ? 'hover:border-indigo-200 active:bg-gray-50 cursor-pointer' : ''
      } ${className}`}
    >
      <div className="flex items-start gap-3">
        {Icon && (
          <div className={`${iconBg} ${compact ? 'w-8 h-8' : 'w-10 h-10'} rounded-lg flex items-center justify-center flex-shrink-0`}>
            <Icon size={compact ? 16 : 20} className={iconColor} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className={`text-gray-500 truncate ${compact ? 'text-[11px]' : 'text-xs'}`}>{label}</p>
          <p className={`font-semibold text-gray-900 ${compact ? 'text-lg' : 'text-xl sm:text-2xl'}`}>{value}</p>
          {trend && (
            <div className={`flex items-center gap-1 mt-0.5 text-xs ${trend.positive ? 'text-green-600' : 'text-red-600'}`}>
              {trend.positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              <span>{trend.value}</span>
            </div>
          )}
          {subtitle && <p className="text-xs text-gray-400 mt-0.5 truncate">{subtitle}</p>}
        </div>
      </div>
    </Wrapper>
  )
}
